import * as React from 'react';
import Link from '@mui/material/Link';
import { DataGrid, GridRowId, GridSortModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import backendApi from '../services/axios.auth.wrapper';

import formatISO from 'date-fns/formatISO';
import { useTheme } from '@mui/material/styles';

const baseURL = process.env.REACT_APP_BACKEND_URL;
// Generate Order Data
interface SegmentsModel {
  segment_name: string;
  camera_id: number;
  site_name__site_name: string;
  video_name__video_timestamp: string;
  video_name: string;
  track_name: string;
  crop_name: string;
  obj_id: number;
  duration: number;
  bucket_name: string;
  species_name__species_name: string;
  segment_rendered: boolean;
  track_rendered: boolean;
}

export interface SignedUrl {
  object_name: string;
  bucket_name: string;
}

export default function Segments(props: {
  site: any;
  camera: any;
  dateRange: any;
  onSegmentChange: any;
  onTrackChange: any;
  onSegmentMp4Change: any;
  selectedOrders: string[];
  selectedSpecies: string[];
}) {
  const theme = useTheme();
  // Signed URL States
  const [segmentUrl, setSegmentUrl] = React.useState<SignedUrl | null>(null);
  const [trackUrl, setTrackUrl] = React.useState<SignedUrl | null>(null);
  // MUI Data Grid state
  const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'segment_timestamp', sort: 'desc' },
  ]);

  const [currentCameraRow, setCurrentCameraRow] = React.useState<SegmentsModel | null>(null);
  const ordersKey = React.useMemo(() => (props.selectedOrders ?? []).join('|'), [props.selectedOrders]);
  const speciesKey = React.useMemo(() => (props.selectedSpecies ?? []).join('|'), [props.selectedSpecies]);

  const { isError, data, isFetching } = useQuery(
    [
      'segments-data',
      props.dateRange,
      props.site,
      props.camera,
      paginationModel.page,
      paginationModel.pageSize,
      ordersKey,
      speciesKey,
      sortModel,
    ],
    async () => {
      if (
        props.dateRange.startDate &&
        props.dateRange.endDate &&
        props.dateRange.startDate <= props.dateRange.endDate &&
        props.site &&
        props.camera &&
        Array.isArray(props.camera) &&
        props.camera.length > 0
      ) {
        const response = await backendApi.post(`${baseURL}/api/segments/`, {
          start_date: formatISO(props.dateRange.startDate, { representation: 'date' }),
          end_date: formatISO(props.dateRange.endDate, { representation: 'date' }),
          site_id: props.site,
          camera_ids: props.camera,
          species_order: props.selectedOrders ?? [],
          species_name: props.selectedSpecies ?? [],
          page: paginationModel.page + 1,
          page_size: paginationModel.pageSize,
          sort_field: sortModel[0]?.field,
          sort_direction: sortModel[0]?.sort,
        });

        return response.data;
      }
      return null;
    },
    { staleTime: 1000 * 60 * 60, keepPreviousData: true, enabled: !!props.site && !!props.camera && Array.isArray(props.camera) && props.camera.length > 0 },
  );

  useQuery(
    ['segment-url', segmentUrl],
    async () => {
      if (!segmentUrl || !segmentUrl.bucket_name) return null;
      const response = await backendApi.post(`${baseURL}/api/signedurl`, segmentUrl);
      return response.data ?? null;
    },
    {
      enabled: Boolean(segmentUrl && segmentUrl.bucket_name),
      onSuccess: (data) => {
        if (data && currentCameraRow?.camera_id != null) {
          props.onSegmentChange({
            data,
            camera_id: currentCameraRow.camera_id,
          });
        }
      },
      onError: (error) => console.error("Failed to fetch segment signed URL:", error),
    }
  );

  useQuery(
    ['tracks-url', trackUrl],
    async () => {
      if (!trackUrl || !trackUrl.bucket_name) return null;
      const response = await backendApi.post(`${baseURL}/api/signedurl`, trackUrl);
      return response.data ?? null;
    },
    {
      enabled: Boolean(trackUrl && trackUrl.bucket_name),
      onSuccess: (data) => {
        if (data && currentCameraRow?.camera_id != null) {
          props.onTrackChange({
            data,
            camera_id: currentCameraRow.camera_id,
            segment_name: currentCameraRow.segment_name
          });
        }
      },
      onError: (error) => console.error("Failed to fetch track signed URL:", error),
    }
  );

  const fetchSignedUrl = (row: SegmentsModel) => {
    if (!row || !row.bucket_name || !row.segment_name) {
      // Clear all details when row data is invalid
      setCurrentCameraRow(null);
      setSegmentUrl(null);
      setTrackUrl(null);
      props.onSegmentChange(null);
      props.onTrackChange(null);
      props.onSegmentMp4Change({
        bucket_name: '',
        object_name: '',
      });
      return;
    }

    setCurrentCameraRow(row);

    if (row.segment_rendered) {
      setSegmentUrl({
        bucket_name: row.bucket_name,
        object_name: `video-segments/${row.segment_name.replace('_demo', '')}`,
      });
    } else {
      setSegmentUrl(null);
      props.onSegmentChange(null);
    }

    if (row.track_rendered) {
      setTrackUrl({
        bucket_name: row.bucket_name,
        object_name: `rendered-tracks/${row.segment_name.replace('_demo', '').replace('segment.webm', 'track.jpg')}`,
      });
    } else {
      setTrackUrl(null);
      props.onTrackChange(null);
    }

    const segmentMp4Object = `video-segments/${row.segment_name.split('.')[0].replace('_demo', '')}.mp4`;
    props.onSegmentMp4Change({
      bucket_name: row.bucket_name,
      object_name: segmentMp4Object,
    });
  };

  React.useEffect(() => {
    if (data?.results) {
      if (data.results.length === 0) {
        // Clear all details when no data is available
        setCurrentCameraRow(null);
        setSegmentUrl(null);
        setTrackUrl(null);
        props.onSegmentChange(null);
        props.onTrackChange(null);
        props.onSegmentMp4Change({
          bucket_name: '',
          object_name: '',
        });
      } else {
        fetchSignedUrl(data.results[0] as SegmentsModel);
      }
    } else {
      // Clear state when data is null/undefined
      setCurrentCameraRow(null);
      setSegmentUrl(null);
      setTrackUrl(null);
      props.onSegmentChange(null);
      props.onTrackChange(null);
      props.onSegmentMp4Change({
        bucket_name: '',
        object_name: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isError) {
    return <span>Error: Failed to load data</span>;
  }

  const rows = data?.results ?? [];
  const rowCount = data?.total ?? 0;

  const columns = [
    {
      field: 'segment_timestamp',
      headerName: 'Detection Timestamp',
      minWidth: 220,
      flex: 2,
      renderCell: (cellValues: any) => {
        return (
          <Link
            href="#"
            color={theme.palette.text.primary}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault();
              if (cellValues.row && cellValues.row.segment_name) {
                setSelectionModel([cellValues.id]);
                fetchSignedUrl(cellValues.row);
              }
            }}
          >
            {new Date(cellValues.value).toLocaleString()}
          </Link>
        );
      },
    },
    {
      field: 'site_name__site_name',
      headerName: 'Site',
      minWidth: 120,
      flex: 1,
      renderCell: (cellValues: any) => {
        return cellValues.value === 'zephyrus' ? 'Zefyros' : cellValues.value;
      },
    },
    {
      field: 'duration',
      headerName: 'Duration',
      minWidth: 100,
      maxWidth: 120,
      sortComparator: (v1: any, v2: any, param1: any, param2: any) =>
        param1.api.getCellValue(param1.id, 'duration') -
        param2.api.getCellValue(param2.id, 'duration'),

      renderCell: (cellValues: any) => {
        return Number(cellValues.value).toFixed(2);
      },
    },
    {
      field: 'species_name__species_name',
      headerName: 'Species',
      minWidth: 140,
      flex: 1.5,
    },
    {
      field: 'track_rendered',
      headerName: 'Image',
      minWidth: 120,
      flex: 1,
      renderCell: (cellValues: any) => {
        return (
          <span style={{ color: cellValues.value ? '#4caf50' : '#757575' }}>
            {cellValues.value ? 'Rendered' : 'Unrendered'}
          </span>
        );
      },
    },
    {
      field: 'segment_rendered',
      headerName: 'Video',
      minWidth: 120,
      flex: 1,
      renderCell: (cellValues: any) => {
        return (
          <span style={{ color: cellValues.value ? '#4caf50' : '#757575' }}>
            {cellValues.value ? 'Rendered' : 'Unrendered'}
          </span>
        );
      },
    },
  ];
  const showLoadingOverlay = isFetching;
  if (props.dateRange.startDate && props.dateRange.endDate) {
    return (
      <React.Fragment>
        <div style={{ height: 650, width: '100%' }}>
          <DataGrid
            getRowId={(row: any) =>
              row.segment_name || `${row.camera_id}-${row.segment_timestamp}`
            }
            columns={columns}
            rowSelectionModel={selectionModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => {
              setSortModel(newSortModel);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            rows={rows}
            loading={showLoadingOverlay}
          />
        </div>
      </React.Fragment>
    );
  }
  return <></>;
}
